import {
  MessageBar,
  MessageBarType,
  TooltipHost
} from '@fluentui/react';
import {FC, useCallback, useEffect, useRef, useState} from 'react';

import {HeadingWithDecoration} from '../../../common/HeadingWithDecoration';
import {MiddleTopBarUnitManage} from '../../../common/MiddleTopBarUnitManage';
import {TheButton} from '../../../common/TheButton';
import {TreeBuilderFull} from '../../../common/TreeBuilder';
import styles from './UnitManagementProcessDetails.module.scss';
import {useUnitManagementProcessesDetails} from './useUnitManagementProcessesDetails';
import {ModalWithDividedRequests} from '../../../common/ModalWithDividedRequests';
import {scrollIntoElement} from '../../../../helpers/visual';
import {Helmet} from 'react-helmet';
import React from 'react';
import { PublishProcessCommentModal } from '../../../common/PublishProcessCommentModal/PublishProcessCommentModal';

export const UnitManagementProcessesDetails: FC = () => {
  const {
    unitId,
    unitTitle,
    columns,
    itemsFlat,
    revealedMasterProcessIds,
    isAnythingLoading,
    isInvalid,
    errorLabel,
    getBulkModel,
    onCancel,
    onSave,
    onSubmit,
    onSubmitWithComment,
    onDismiss,
    isModalVisible,
    progressStatus
  } = useUnitManagementProcessesDetails();

  const errorMsgBar = useRef<HTMLDivElement>(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [processPublicationComment, setProcessPublicationComment] = React.useState('');
  const onProcessPublicationCommentChange = useCallback(
    (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
      if (!newValue || newValue.length <= 1024) setProcessPublicationComment(newValue || '');
    },
    []
  );
  const onModalWithCommentClose = useCallback(() => setIsCommentModalOpen(false), []);
  const onApproveModal = (sendNotification: boolean) => {
    onSubmitWithComment(processPublicationComment, sendNotification);
    setIsCommentModalOpen(false);
    setProcessPublicationComment('');
  };

  useEffect(() => {
    if (isInvalid) {
      scrollIntoElement(errorMsgBar);
    }
  }, [isInvalid]);
  return (
    <main className={'noPaddingTop'}>
      {unitTitle && (
        <Helmet>
          <title>IMS {unitTitle} Process Details</title>
        </Helmet>
      )}
      <MiddleTopBarUnitManage notUseHelmet unitId={unitId} active={'processesDetails'} />
      <HeadingWithDecoration text="Processes Details" />
      {isInvalid && (
        <MessageBar
          className={styles.errorMessage}
          messageBarType={MessageBarType.error}
          ref={errorMsgBar}>
          {errorLabel}
        </MessageBar>
      )}
      <TreeBuilderFull
        bulkHeader="Bulk edit Manage Process Details"
        editColumnPosition={1}
        getBulkModel={getBulkModel}
        columns={columns}
        itemsFlat={itemsFlat}
        onCancel={onCancel}
        turnOffEditNonProcess={true}
        onSubmit={onSave}
        additionalButtons={
          <>
            <TooltipHost content={'Publish minor change. No notification will be sent.'}>
              <TheButton
                primary
                className={styles.split}
                disabled={isAnythingLoading}
                iconProps={{
                  iconName: 'MailPause'
                }}
                onClick={onSubmit}>
                Publish
              </TheButton>
            </TooltipHost>
            <TooltipHost
              content={
                'Publish standard or major change. Next process update will change, notification will be sent and comment can be added.'
              }>
              <TheButton
                primary
                className={styles.split}
                disabled={isAnythingLoading}
                iconProps={{
                  iconName: 'MailAttached'
                }}
                onClick={() => {
                  setIsCommentModalOpen(true);
                }}>
                Publish and confirm process validity
              </TheButton>
            </TooltipHost>
          </>
        }
        isLoading={isAnythingLoading}
        initialOpen={revealedMasterProcessIds}
        keepInView
        exportToExcel
        excelFileName="Exported Manage Process Details"
        filterDeleted
      />

      <PublishProcessCommentModal
          isVisible={isCommentModalOpen}
          dismiss={onModalWithCommentClose}
          processPublicationComment={processPublicationComment}
          onProcessPublicationCommentChange={onProcessPublicationCommentChange}
          onApproveModal={onApproveModal}
      />
      <ModalWithDividedRequests
        header="Information"
        dismiss={onDismiss}
        isVisible={isModalVisible}
        couldBeClosed={false}
        progressStatus={progressStatus}
        body="Due to technological reasons, the query will be sent in packages. Please, be patient."
      />
    </main>
  );
};
